import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { UserCheck, ShieldCheck, MessageSquare, ArrowRight, Lock, CreditCard } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function ExpertReviewCTA({ checkId }) {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ stripe_enabled: true, razorpay_enabled: false, packages: [] });
  const [packageId, setPackageId] = useState("expert_review");
  const [form, setForm] = useState({
    contact_name: user?.name || "",
    contact_email: user?.email || "",
    contact_phone: "",
    notes: "",
    provider: "stripe",
    mode: "pay", // "pay" or "credit"
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/config");
        setConfig(data);
        setForm((f) => ({ ...f, provider: data.razorpay_enabled ? f.provider : "stripe" }));
      } catch {}
    })();
  }, []);

  const availableCredits = user?.workspace?.review_credits ?? user?.review_credits ?? 0;
  const creditSource = user?.workspace ? "team" : "personal";

  useEffect(() => {
    if (open) {
      if (availableCredits > 0) {
        setForm((f) => ({ ...f, mode: "credit" }));
      } else {
        setForm((f) => ({ ...f, mode: "pay" }));
      }
    }
  }, [open, availableCredits]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const currentPkg = config.packages?.find((p) => p.id === packageId) ||
                     { id: "expert_review", amount: 100, currency: "GBP", credits: 1, name: "Expert Human Review" };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (form.mode === "credit") {
        await redeemCredit();
        return;
      }
      if (form.provider === "razorpay") {
        await payWithRazorpay();
      } else {
        await payWithStripe();
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const redeemCredit = async () => {
    const { data } = await api.post("/expert-reviews/redeem", {
      check_id: checkId,
      contact_name: form.contact_name,
      contact_email: form.contact_email,
      contact_phone: form.contact_phone || null,
      notes: form.notes || null,
    });
    toast.success(`Credit redeemed — ${data.credits_remaining} left.`);
    await refresh();
    setOpen(false);
    setLoading(false);
    navigate("/app/history");
  };

  const payWithStripe = async () => {
    const { data } = await api.post("/payments/expert-review/checkout", {
      check_id: checkId,
      contact_name: form.contact_name,
      contact_email: form.contact_email,
      contact_phone: form.contact_phone || null,
      notes: form.notes || null,
      origin_url: window.location.origin,
      package_id: packageId,
    });
    if (data.checkout_url) {
      window.location.href = data.checkout_url;
    } else {
      toast.error("Could not start Stripe checkout.");
      setLoading(false);
    }
  };

  const payWithRazorpay = async () => {
    const ok = await loadRazorpayScript();
    if (!ok) {
      toast.error("Could not load Razorpay. Check your connection and try again.");
      setLoading(false);
      return;
    }
    const { data } = await api.post("/payments/expert-review/razorpay/order", {
      check_id: checkId,
      contact_name: form.contact_name,
      contact_email: form.contact_email,
      contact_phone: form.contact_phone || null,
      notes: form.notes || null,
      origin_url: window.location.origin,
      package_id: packageId,
    });

    const rzp = new window.Razorpay({
      key: data.key_id,
      amount: data.amount,
      currency: data.currency,
      order_id: data.order_id,
      name: "CoS Verify",
      description: "Expert Human Review — UKVI CoS",
      prefill: data.prefill,
      theme: { color: "#0F172A" },
      handler: async (resp) => {
        try {
          await api.post("/payments/expert-review/razorpay/verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          toast.success("Payment received — thank you!");
          navigate(`/payment/success?session_id=${resp.razorpay_order_id}&provider=razorpay`);
        } catch (err) {
          toast.error("Payment verification failed. Please contact us.");
          setLoading(false);
        }
      },
      modal: {
        ondismiss: () => setLoading(false),
      },
    });
    rzp.on("payment.failed", () => {
      toast.error("Payment failed. Please try again.");
      setLoading(false);
    });
    rzp.open();
  };

  return (
    <>
      <div className="border border-[#0F172A] bg-[#0F172A] text-white rounded-sm p-5 mt-6 grid lg:grid-cols-12 gap-4 items-center" data-testid="expert-review-cta">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-2 border border-white/30 px-2 py-0.5 rounded-sm">
              <UserCheck className="w-3 h-3 text-white" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">
                Human · in the loop
              </span>
            </div>
            {availableCredits > 0 && (
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm bg-emerald-400/20 text-emerald-200 border border-emerald-400/50"
                    data-testid="credits-badge">
                {availableCredits} {creditSource === "team" ? "team" : ""} credit{availableCredits === 1 ? "" : "s"} available
              </span>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 className="font-heading text-xl lg:text-2xl font-bold tracking-tight">
              Get a human expert to double-check this CoS
            </h3>
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">
              · 1:1 reply within 2 UK business days
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-white/75">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Manual re-check</span>
            <span className="inline-flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> 1:1 communication</span>
            <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Confidential</span>
          </div>
        </div>
        <div className="lg:col-span-4 flex items-center gap-4 lg:justify-end">
          {availableCredits === 0 && (
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">From</div>
              <div className="font-heading text-3xl font-bold tracking-tighter leading-none mt-0.5">
                £100 <span className="font-mono text-xs text-white/60">GBP</span>
              </div>
            </div>
          )}
          <Button
            onClick={() => setOpen(true)}
            className="h-11 rounded-sm bg-white text-[#0F172A] hover:bg-zinc-100 whitespace-nowrap"
            data-testid="expert-review-book-btn"
          >
            {availableCredits > 0 ? "Redeem or buy more" : "Book expert review"} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="expert-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              Book expert review
            </DialogTitle>
            <DialogDescription>
              How should our team reach you? We aim to respond within 2 UK business days.
            </DialogDescription>
          </DialogHeader>

          {/* Mode picker: credit vs pay */}
          <div className="grid grid-cols-2 gap-2 border border-zinc-200 rounded-sm p-1 bg-zinc-50">
            <button type="button"
              onClick={() => setForm((f) => ({ ...f, mode: "credit" }))}
              disabled={availableCredits === 0}
              data-testid="mode-credit"
              className={`text-left px-3 py-2 rounded-sm text-sm ${
                form.mode === "credit"
                  ? "bg-white shadow-sm font-semibold text-[#0F172A]"
                  : "text-zinc-600 hover:text-zinc-900"
              } ${availableCredits === 0 ? "opacity-40 cursor-not-allowed" : ""}`}>
              Use credit
              <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">
                {availableCredits} {creditSource === "team" ? "team" : ""} available
              </div>
            </button>
            <button type="button"
              onClick={() => setForm((f) => ({ ...f, mode: "pay" }))}
              data-testid="mode-pay"
              className={`text-left px-3 py-2 rounded-sm text-sm ${
                form.mode === "pay"
                  ? "bg-white shadow-sm font-semibold text-[#0F172A]"
                  : "text-zinc-600 hover:text-zinc-900"
              }`}>
              Buy / Pay
              <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-0.5">
                Single or bundle
              </div>
            </button>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <FieldRow>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500" htmlFor="rvname">Your name</Label>
              <Input id="rvname" required value={form.contact_name} onChange={update("contact_name")}
                     className="rounded-sm h-10" data-testid="er-name-input" />
            </FieldRow>
            <FieldRow>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500" htmlFor="rvemail">Email</Label>
              <Input id="rvemail" type="email" required value={form.contact_email} onChange={update("contact_email")}
                     className="rounded-sm h-10" data-testid="er-email-input" />
            </FieldRow>
            <FieldRow>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500" htmlFor="rvphone">Phone / WhatsApp (optional)</Label>
              <Input id="rvphone" value={form.contact_phone} onChange={update("contact_phone")}
                     className="rounded-sm h-10" data-testid="er-phone-input" placeholder="+44 …" />
            </FieldRow>
            <FieldRow>
              <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500" htmlFor="rvnotes">Notes for our team (optional)</Label>
              <Textarea id="rvnotes" rows={3} value={form.notes} onChange={update("notes")}
                        className="rounded-sm" placeholder="Anything specific we should look at?" data-testid="er-notes-input" />
            </FieldRow>

            {form.mode === "pay" && (
              <>
                <FieldRow>
                  <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Choose package</Label>
                  <div className="grid grid-cols-2 gap-2" data-testid="er-package-picker">
                    {(config.packages || []).map((p) => (
                      <ProviderPill key={p.id}
                        selected={packageId === p.id}
                        onClick={() => setPackageId(p.id)}
                        label={p.id === "bundle_3" ? "3-Pack · Save £50" : "Single"}
                        sub={`£${p.amount} · ${p.credits} credit${p.credits === 1 ? "" : "s"}`}
                        testid={`er-pkg-${p.id}`} />
                    ))}
                  </div>
                </FieldRow>

                {config.razorpay_enabled && (
                  <FieldRow>
                    <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Payment method</Label>
                    <div className="grid grid-cols-2 gap-2" data-testid="er-provider-picker">
                      <ProviderPill selected={form.provider === "stripe"}
                                    onClick={() => setForm((f) => ({ ...f, provider: "stripe" }))}
                                    label="Stripe" sub="Card · Wallet" testid="er-provider-stripe" />
                      <ProviderPill selected={form.provider === "razorpay"}
                                    onClick={() => setForm((f) => ({ ...f, provider: "razorpay" }))}
                                    label="Razorpay" sub="Card · UPI · Netbanking" testid="er-provider-razorpay" />
                    </div>
                  </FieldRow>
                )}
              </>
            )}

            <DialogFooter className="pt-2">
              <Button type="submit" disabled={loading}
                      className="w-full h-11 rounded-sm bg-[#0F172A] hover:bg-[#0F172A]/90"
                      data-testid="er-pay-btn">
                <CreditCard className="w-4 h-4" />
                {loading ? "Processing…" :
                  form.mode === "credit" ? "Redeem 1 credit & book review" :
                  form.provider === "razorpay" ? `Pay £${currentPkg.amount} with Razorpay` :
                  `Pay £${currentPkg.amount} with Stripe`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProviderPill({ selected, onClick, label, sub, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`text-left px-3 py-2 rounded-sm border transition-colors ${
        selected ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-zinc-300 bg-white text-zinc-800 hover:border-zinc-500"
      }`}>
      <div className="font-heading font-semibold text-sm">{label}</div>
      <div className={`font-mono text-[10px] uppercase tracking-[0.15em] ${selected ? "text-white/70" : "text-zinc-500"}`}>
        {sub}
      </div>
    </button>
  );
}

function FieldRow({ children }) {
  return <div className="space-y-1.5">{children}</div>;
}
